<template>
  <main-large-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :is-valid="computedIsValid"
    :title="computedTitle"
    :agree-text="computedAgreeText"
    dialog-width="60%"
    min-body-height="300px"
    max-height="60vh"
    no-paddings-y
    no-paddings-x
    @agree="handleAttachments"
    @modalClosed="onClose"
    :loading="loading"
  >
    <template #body>
      <v-tabs v-model="currentTab" class="fill-height d-flex flex-column" hide-slider color="font">
        <v-tab
          v-for="tab in tabs"
          :key="tab.value"
          v-text="tab.text"
          active-class="activeTab"
          class="font--text"
          style="text-transform: none;"
        ></v-tab>
        <v-tabs-items v-model="currentTab" class="fill-height">
          <v-tab-item style="height: inherit">
            <v-form v-model="isValid">
              <add-attachment-tab
                ref="addAttachment"
                :features="features"
                @fileUploaded="fileUploaded"
                @fileError="loading = false"
                :expanded="expanded"
                :formSchema="formSchema"
              />
            </v-form>
          </v-tab-item>
          <v-tab-item style="height: inherit">
            <assign-attachment-tab
              :attachments="filteredAttachments"
              @selectAttachments="setSelected"
              :selected="selectedAttachments"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </template>
  </main-large-dialog>
</template>

<script>
import AssignAttachmentTab from '@/components/AssignAttachmentTab';
import AddAttachmentTab from '@/components/AddAttachmentTab';
import { call, get } from 'vuex-pathify';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'AttachmentsDialog',
  components: { AddAttachmentTab, AssignAttachmentTab, MainLargeDialog },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    features: {
      type: Array,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    formSchema: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  data: vm => ({
    currentTab: 0,
    isValid: false,
    tabs: [
      { text: vm.$i18n.t('dialog.title.add'), value: 'add' },
      { text: vm.$i18n.t('dialog.title.assign'), value: 'assign' },
    ],
    selectedAttachments: [],
    loading: false,
  }),
  computed: {
    attachmentsForFeature: get('relations/attachmentsForFeature'),
    computedIsValid() {
      return this.isValid || this.currentTab !== 0;
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    attachments: get('relations/attachments'),
    currentLayerDataSourceName: get('layers/currentLayer@data_source_name'),
    featureId() {
      return this.$route.params.fid;
    },
    filteredAttachments() {
      const attachmentsForFeatureIds = this.attachmentsForFeature.map(attachment => attachment.feature_id);
      return this.attachments.filter(attachment => !attachmentsForFeatureIds.includes(attachment.id));
    },
    computedTitle() {
      return this.currentTab === 1
        ? this.$i18n.t('dialog.attachments.assignAttachment')
        : this.$i18n.t('dialog.attachments.addAttachment');
    },
    computedAgreeText() {
      return this.currentTab === 1 ? 'dialog.title.assign' : 'dialog.title.send';
    },
    handleAttachments() {
      return this.currentTab === 1 ? this.handleAssign : this.handleAdd;
    },
  },
  methods: {
    getAttachments: call('relations/getAttachments'),
    getRelations: call('relations/getRelations'),
    assignAttachments: call('relations/assignAttachments'),
    async handleAdd() {
      this.loading = true;
      if (this.$refs.addAttachment.$refs.file.$refs.dropzone.getAcceptedFiles().length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.filesRequired'),
        });
      } else {
        this.$refs.addAttachment.uploadFiles();
      }
    },
    async fileUploaded() {
      if (this.currentLayerDataSourceName && (this.featureId || this.featureId === 0)) {
        const payload = {
          data_source_name: this.currentLayerDataSourceName,
          feature_id: this.featureId,
        };
        await Promise.all([this.getRelations(payload), await this.getAttachments()]);
      }
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshFormData');
      this.computedIsVisible = false;
      this.loading = false;
    },
    setSelected(data) {
      this.selectedAttachments = data;
    },
    async handleAssign() {
      this.loading = true;
      const payload = this.selectedAttachments.map(r => {
        return {
          attachment_id: r.id,
          objects: this.features,
        };
      });
      await this.assignAttachments(payload);
      if (this.currentLayerDataSourceName && (this.featureId || this.featureId === 0)) {
        const payload = {
          data_source_name: this.currentLayerDataSourceName,
          feature_id: this.featureId,
        };
        await this.getRelations(payload);
      }
      this.loading = false;
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshFormData');
      this.$root.$emit('fileUploaded');
      this.$emit('filesAssigned', this.selectedAttachments);
      this.computedIsVisible = false;
      this.setSelected([]);
    },
    onClose() {
      this.setSelected([]);
      this.$refs.addAttachment?.$refs.file?.$refs.dropzone.removeAllFiles();
    },
  },
  mounted() {
    this.getAttachments();
  },
};
</script>

<style lang="scss" scoped>
.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}
::v-deep {
  .v-input--selection-controls__input .v-icon {
    font-size: 19px;
  }
  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12) !important;
  }
}
</style>
